import type { CommerceClient, Cart, ConnectivityStatus, SessionInfo, StoreWithOpeningHours } from '@altazion/commerce-sdk-core'
import type Handlebars from 'handlebars'

import {
  registerAltazionExtension,
  type AltazionActionEventDetail,
  type AltazionCartEventDetail,
  type AltazionExtensionHandle,
  type AltazionMarketingEventDetail,
  type AltazionSessionEventDetail,
  type AltazionStoresEventDetail,
} from './extensions/altazion-auth'
import { registerPriceHelpers } from './helpers/price'
import { registerProductHelpers } from './helpers/product'
import { registerCartHelpers } from './helpers/cart'
import { createAltazionDeclarativeRenderer } from './rendering/declarative-renderer'
import { initStorePicker } from './modules/store-picker'

// Templates pré-compilés importés en tant que chaînes brutes
import productCardTemplate from './templates/product-card.hbs?raw'
import cartMiniTemplate from './templates/cart-mini.hbs?raw'
import productListTemplate from './templates/product-list.hbs?raw'

export type { Cart }

type SelectorInput = string | string[]

export interface AltazionTerminalModeConfig {
  /** Zones interactives à masquer en mode hors ligne. */
  interactiveSelectors?: SelectorInput
  /** Zones passives à afficher en mode hors ligne. */
  offlineScreenSelectors?: SelectorInput
}

export interface AltazionConnectivityEventDetail {
  status: ConnectivityStatus
  isOnline: boolean
  isOffline: boolean
  root: Element
}

export interface AltazionHtmxConfig {
  /** Instance CommerceClient déjà initialisée */
  client: CommerceClient
  /**
   * Instance Handlebars (global ou importée).
   * Si omis, les helpers ne sont pas enregistrés.
   */
  handlebars?: typeof Handlebars
  /**
   * Sélecteur CSS de l'élément DOM à basculer avec la classe `altz-offline`
   * lorsque la connectivité est perdue/restaurée.
   * @default 'body'
   */
  offlineSelector?: string
  /** Configuration UI du mode hors ligne borne. */
  terminalMode?: AltazionTerminalModeConfig
}

export interface AltazionHtmxInstance {
  /** Templates Handlebars compilés prêts à l'emploi */
  templates: {
    productCard: HandlebarsTemplateDelegate
    cartMini: HandlebarsTemplateDelegate
    productList: HandlebarsTemplateDelegate
  }
  /** Détruit les listeners et libère les ressources */
  dispose(): void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HandlebarsTemplateDelegate = (context?: any) => string

/**
 * Initialise le SDK HTMX Altazion.
 *
 * - Enregistre l'extension HTMX (headers contexte) si htmx est présent
 * - Enregistre les helpers Handlebars (prix, produits, panier) si hbs est fourni
 * - Active l'indicateur d'état offline sur le DOM
 * - Retourne les templates compilés
 *
 * @example
 * ```ts
 * import Handlebars from 'handlebars'
 * const sdk = init({ client, handlebars: Handlebars })
 * const html = sdk.templates.productCard(product)
 * document.getElementById('product')!.innerHTML = html
 * ```
 */
export function initAltazionHtmx(config: AltazionHtmxConfig): AltazionHtmxInstance {
  const {
    client,
    handlebars,
    offlineSelector = 'body',
    terminalMode,
  } = config

  // 1. Extension HTMX — headers de contexte + actions panier déclaratives
  const extensionHandle: AltazionExtensionHandle = registerAltazionExtension(client)

  // 2. HTMX config globale — transmettre les cookies sur les requêtes XHR HTMX
  if (typeof window !== 'undefined' && window.htmx) {
    // @ts-expect-error — htmx.config n'est pas dans les types window
    window.htmx.config = window.htmx.config ?? {}
    // @ts-expect-error
    window.htmx.config.withCredentials = true
  }

  // 3. Helpers Handlebars
  const defaults = {
    locale: client.context.locale,
    currency: client.context.currency,
  }

  const handlebarsRuntime = handlebars
    ?? (typeof window !== 'undefined'
      ? (window as Window & { Handlebars?: typeof Handlebars }).Handlebars
      : undefined)

  const declarativeRenderer = createAltazionDeclarativeRenderer(handlebarsRuntime)
  const syncSlotFromEvent = (detail: AltazionActionEventDetail): void => {
    const slotName = detail.element.getAttribute('hx-altazion-data-slot')?.trim()
    if (!slotName) {
      return
    }

    declarativeRenderer.updateSlot(slotName, detail.result ?? null)
  }

  // Getter réactif sur le panier actuel utilisé par les helpers Handlebars.
  let currentCart: Cart | null = null
  const getCart = (): Cart | null => currentCart

  const syncCartFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionCartEventDetail<Cart>>
    if (customEvent.detail.result) {
      currentCart = customEvent.detail.result
      declarativeRenderer.update('cart', currentCart)
    }
    syncSlotFromEvent(customEvent.detail)
  }

  const syncSessionFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionSessionEventDetail<SessionInfo>>
    declarativeRenderer.update('session', customEvent.detail.result ?? null)
    syncSlotFromEvent(customEvent.detail)
  }

  const syncStoreFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionStoresEventDetail<StoreWithOpeningHours>>
    declarativeRenderer.update('store', customEvent.detail.result ?? null)
    syncSlotFromEvent(customEvent.detail)
  }

  const syncStoresFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionStoresEventDetail<StoreWithOpeningHours[]>>
    declarativeRenderer.update('stores', Array.isArray(customEvent.detail.result) ? customEvent.detail.result : [])
    syncSlotFromEvent(customEvent.detail)
  }

  const syncMarketingItemFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionMarketingEventDetail>
    declarativeRenderer.update('marketingItem', Array.isArray(customEvent.detail.result) ? null : customEvent.detail.result ?? null)
    syncSlotFromEvent(customEvent.detail)
  }

  const syncMarketingItemsFromEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<AltazionMarketingEventDetail>
    declarativeRenderer.update('marketingItems', Array.isArray(customEvent.detail.result) ? customEvent.detail.result : [])
    syncSlotFromEvent(customEvent.detail)
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('altazion:cart:loaded', syncCartFromEvent)
    document.addEventListener('altazion:cart:updated', syncCartFromEvent)
    document.addEventListener('altazion:session:loaded', syncSessionFromEvent)
    document.addEventListener('altazion:stores:store-loaded', syncStoreFromEvent)
    document.addEventListener('altazion:stores:stores-loaded', syncStoresFromEvent)
    document.addEventListener('altazion:marketing:item-loaded', syncMarketingItemFromEvent)
    document.addEventListener('altazion:marketing:items-loaded', syncMarketingItemsFromEvent)
  }

  void client.cart.getCart()
    .then((cart: Cart) => {
      currentCart = cart
      declarativeRenderer.update('cart', cart)
    })
    .catch(() => { /* panier non disponible au chargement */ })

  if (handlebars) {
    registerPriceHelpers(handlebars, defaults)
    registerProductHelpers(handlebars, client.context.siteUrl ?? '')
    registerCartHelpers(handlebars, getCart, defaults)
  }

  declarativeRenderer.render()

  // 4. Store picker
  const disposeStorePicker = initStorePicker(client, handlebarsRuntime)

  // 5. Indicateur offline DOM + mode borne
  const offlineEl = typeof document !== 'undefined'
    ? document.querySelector(offlineSelector) ?? document.body
    : null

  let lastConnectivityStatus: ConnectivityStatus | null = null

  const updateOfflineClass = (): void => {
    if (!offlineEl) {
      return
    }

    const status: ConnectivityStatus = client.isOffline ? 'offline' : 'online'
    const isOffline = status === 'offline'

    offlineEl.classList.toggle('altz-offline', isOffline)
    offlineEl.classList.toggle('altz-terminal-offline', isOffline)
    offlineEl.classList.toggle('altz-terminal-online', !isOffline)
    offlineEl.setAttribute('data-altazion-terminal-state', status)

    updateManagedVisibility(resolveManagedElements(terminalMode?.interactiveSelectors), isOffline, 'altz-terminal-interactive-hidden')
    updateManagedVisibility(resolveManagedElements(terminalMode?.offlineScreenSelectors), !isOffline, 'altz-terminal-offline-screen-hidden')

    if (lastConnectivityStatus === status) {
      return
    }

    lastConnectivityStatus = status
    dispatchConnectivityEvent(offlineEl, 'altazion:connectivity:changed', status)
    dispatchConnectivityEvent(offlineEl, isOffline ? 'altazion:offline' : 'altazion:online', status)
  }

  // Synchronisation initiale
  updateOfflineClass()

  // Abonnement ConnectivityManager — retourne la fonction de désabonnement
  const unsubscribeConnectivity = client.connectivity?.subscribe(() => updateOfflineClass())

  // 5. Templates compilés (retournés pour usage dans le code applicatif)
  const noHbs = (): HandlebarsTemplateDelegate =>
    () => '<!-- handlebars non initialisé dans @altazion/commerce-sdk-htmx -->'

  const templates = handlebars
    ? {
        productCard: handlebars.compile(productCardTemplate),
        cartMini: handlebars.compile(cartMiniTemplate),
        productList: handlebars.compile(productListTemplate),
      }
    : {
        productCard: noHbs(),
        cartMini: noHbs(),
        productList: noHbs(),
      }

  return {
    templates,
    dispose(): void {
      extensionHandle.dispose()
      if (typeof document !== 'undefined') {
        document.removeEventListener('altazion:cart:loaded', syncCartFromEvent)
        document.removeEventListener('altazion:cart:updated', syncCartFromEvent)
        document.removeEventListener('altazion:session:loaded', syncSessionFromEvent)
        document.removeEventListener('altazion:stores:store-loaded', syncStoreFromEvent)
        document.removeEventListener('altazion:stores:stores-loaded', syncStoresFromEvent)
        document.removeEventListener('altazion:marketing:item-loaded', syncMarketingItemFromEvent)
        document.removeEventListener('altazion:marketing:items-loaded', syncMarketingItemsFromEvent)
      }
      declarativeRenderer.dispose()
      disposeStorePicker()
      unsubscribeConnectivity?.()
    },
  }
}

export const init = initAltazionHtmx

function resolveManagedElements(selectorInput?: SelectorInput): Element[] {
  if (typeof document === 'undefined' || !selectorInput) {
    return []
  }

  const selectors = Array.isArray(selectorInput) ? selectorInput : [selectorInput]
  const elements = new Set<Element>()

  for (const selector of selectors) {
    if (!selector.trim()) {
      continue
    }

    try {
      for (const element of document.querySelectorAll(selector)) {
        elements.add(element)
      }
    } catch {
      // Sélecteur invalide : on ignore silencieusement.
    }
  }

  return [...elements]
}

function updateManagedVisibility(elements: Element[], hidden: boolean, hiddenClassName: string): void {
  for (const element of elements) {
    element.classList.toggle(hiddenClassName, hidden)
    element.setAttribute('aria-hidden', hidden ? 'true' : 'false')

    if (element instanceof HTMLElement) {
      element.hidden = hidden
    } else if (hidden) {
      element.setAttribute('hidden', '')
    } else {
      element.removeAttribute('hidden')
    }
  }
}

function dispatchConnectivityEvent(
  root: Element,
  eventName: string,
  status: ConnectivityStatus
): void {
  const detail: AltazionConnectivityEventDetail = {
    status,
    isOnline: status === 'online',
    isOffline: status === 'offline',
    root,
  }

  root.dispatchEvent(new CustomEvent<AltazionConnectivityEventDetail>(eventName, {
    bubbles: true,
    detail,
  }))
}

// Re-exports helpers pour usage à la carte
export { registerPriceHelpers } from './helpers/price'
export { registerProductHelpers } from './helpers/product'
export { registerCartHelpers } from './helpers/cart'
export {
  registerAltazionExtension,
  registerAltazionAuthExtension,
} from './extensions/altazion-auth'
export type {
  AltazionActionEventDetail,
  AltazionActionErrorDetail,
  AltazionCartActionName,
  AltazionCartEventDetail,
  AltazionExtensionHandle,
  AltazionMarketingActionName,
  AltazionMarketingEventDetail,
  AltazionSessionActionName,
  AltazionSessionEventDetail,
  AltazionStoresActionName,
  AltazionStoresEventDetail,
  AltazionSuccessEventKind,
} from './extensions/altazion-auth'
